// src/components/ProductList.js
import React, { useState } from 'react';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';

const ProductList = ({ 
  products, 
  primaryColor, 
  secondaryColor, 
  contacts, 
  categories, 
  searchTerm,
  language = 'en', // 'en' or 'km'
  onAddToCart // Add to cart function from parent
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Translations
  const translations = {
    en: {
      noProducts: 'No products found',
      noResults: 'No results found for',
      checkBackLater: 'Check back later for new items',
      ourProducts: '🔥 Our Products',
      discover: 'Discover our curated collection',
      found: 'Found',
      products: 'product(s)',
      searchResults: 'Search Results for'
    },
    km: {
      noProducts: 'មិនមានផលិតផល',
      noResults: 'មិនមានលទ្ធផលសម្រាប់',
      checkBackLater: 'សូមត្រឡប់មកវិញនៅពេលក្រោយសម្រាប់ផលិតផលថ្មី',
      ourProducts: '🔥 ផលិតផលរបស់យើង',
      discover: 'ស្វែងយល់ពីបណ្តុំផលិតផលរបស់យើង',
      found: 'បានរកឃើញ',
      products: 'ផលិតផល',
      searchResults: 'លទ្ធផលស្វែងរកសម្រាប់'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  // Filter out invalid products and apply search
  const validProducts = products?.filter(product => product && product.id) || [];
  
  const filteredProducts = searchTerm 
    ? validProducts.filter(product => 
        product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : validProducts;

  if (!filteredProducts || filteredProducts.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-50 rounded-2xl" style={{ fontFamily: getFontFamily() }}>
        <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-box-open text-gray-300 text-4xl"></i>
        </div>
        <p className="text-gray-500 text-lg font-medium" style={{ fontFamily: getFontFamily() }}>
          {t.noProducts}
        </p>
        {searchTerm && (
          <p className="text-gray-400 text-sm mt-2" style={{ fontFamily: getFontFamily() }}>
            {t.noResults} "{searchTerm}"
          </p>
        )}
        <p className="text-gray-400 text-sm mt-2" style={{ fontFamily: getFontFamily() }}>
          {t.checkBackLater}
        </p>
      </div>
    );
  }

  return (
    <>
      <div id="products" className="mb-12" style={{ fontFamily: getFontFamily() }}>
        <div className="mb-8">
          <h2 
            className="text-2xl md:text-3xl font-semibold text-gray-800 tracking-tight"
            style={{ fontFamily: getFontFamily() }}
          >
            {searchTerm ? `${t.searchResults} "${searchTerm}"` : t.ourProducts}
          </h2>
          <span 
            className="text-gray-500 text-sm md:text-base"
            style={{ fontFamily: getFontFamily() }}
          >
            {t.discover}
          </span>
          {searchTerm && (
            <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: getFontFamily() }}>
              {t.found} {filteredProducts.length} {t.products}
            </p>
          )}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              id={`product-${product.id}`}
              product={product}
              onClick={() => setSelectedProduct(product)}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              language={language}
              onAddToCart={onAddToCart}
            />
          ))}
        </div>
      </div>
      
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          contacts={contacts}
          categories={categories}
          language={language}
        />
      )}
    </>
  );
};

export default ProductList;